/* eslint semi: 1 */

import _ from 'lodash';
import utils from '@/globals/utils';

import { proxyException, EitherResponse } from './player-setup-communication';
import { tagsStore } from '../../../../pinia/stores/tagsStore';

/**
 * Transform tags to something consumable for Canvas management
 */
export const tagsToCanvas = (tags) => {
  const response = [];

  for (const ledTag of tags.ledTags ?? []) {
    // it would be also in videowallTags
    if (_.isArray(ledTag.bezels)) {
      continue;
    }

    response.push({
      tag: utils.parseLedTag(ledTag.name),
      bezels: ledTag.bezels,
      types: ['led'],
      inline: ledTag?.inline ?? [],
      color: ledTag?.color ?? []
    });
  }

  for (const videowallTag of tags.videowallTags ?? []) {
    const types = _.isArray(videowallTag.bezels) && videowallTag.bezels.length === 0
      ? ['videowall', 'led']
      : ['videowall'];

    response.push({
      types,
      tag: utils.parseLedTag(videowallTag.name),
      bezels: videowallTag.bezels,
      inline: videowallTag?.inline ?? [],
      color: videowallTag?.color ?? []
    });
  }

  return response;
};

/**
 * The way to use should be the following:
 *   `const canvas = new Canvas(this.$api)`
 * $api is middleware with Axios.
 */
export default class Canvas {
  _api = null;

  constructor (api) {
    this._api = api;
  }

  /**
   * Load tags by type via REST API. Will return something like:
   * ```{ types: ['videowall'],
   *      tag: {
   *        width: Number, height: Number,
   *        name: String,
   *        dimension: `${width}x${height}`,
   *        queryStringValue: `${width}x${height}[x${name}]`,
   *        value: `LED:${width}x${height}[x${name}]`
   *      },
   *      bezels: [{width: Number, height: Number, x: Number, y: Number}],
   *      color: "#ffffff"
   *    }```
   *
   * If inline, it contains the media tags represented in an array format, such as:
   * inline: ['office', 'home', 'gym']
   * The purpose of the inline tag is to associate specific resolutions with corresponding media files for inlining
   */
  async getCanvas (inline = false) {
    const params = {
      type: 'videowallTags,ledTags',
      ext: 1,
      inline: inline ? 1 : undefined
    };
    const { ok: response, error } = await proxyException(() => this._api.get(`${CONFIG.API_BASE_URL}/group/tags`, { params }));

    if (error) {
      return EitherResponse(
        null,
        error.message ?? 'Can\'t load tags LED and Videowall tags'
      );
    }

    return EitherResponse(tagsToCanvas(response.data) ?? []);
  }

  async findExistingCanvas (type, canvasSize) {
    const { ok: canvases, error } = await this.getCanvas();

    if (error) {
      return EitherResponse(
        null,
        error.message ?? 'Can\'t load tags LED and Videowall tags'
      );
    }

    // Find if videowall/led canvas/tag for a resolution already exists
    const existingCanvas = canvases.find((canvas) => {
      return canvas.types.includes(type) && canvasSize === `${canvas.tag.width} ${canvas.tag.height}`;
    });

    return EitherResponse(existingCanvas);
  }

  /**
   * Create a Tag with VideoWall LED attributes if necessary.
   * `bezels` is an array with four attributes, represents mask for bezels:
   *   `width` - width of a rectangle;
   *   `height` - height of the rectangle;
   *   `x` - x position of the rectangle;
   *   `y` - y position of the rectangle.
   * Bezels array is a list of rectangles which would represent a
   * mask on top of a project/media to understand how bezels are configured.
   */
  async _createTag (name, width, height, bezels) {
    if (!width || !height) {
      return EitherResponse(null, 'width or height are not specified correctly to create Tag');
    }
    const resolution = `${width}x${height}`;
    let tagName = `LED:${resolution}`;

    if (name && _.isString(name) && name.trim() !== '') {
      tagName = `${tagName}x${name.trim()}`;
    }

    const payload = {
      name: tagName,
      type: 'led'
    };

    // bezel info, comes from VideoWall configuration
    if (_.isArray(bezels)) {
      payload.canvasInfo = { bezels };
    }

    await tagsStore().createTag(payload);
    const error = tagsStore().error;

    if (!error) {
      return EitherResponse(payload);
    }
    return EitherResponse(null, error);
  }

  /**
   * Create VideoWall tag and possible LED if bezels is empty array
   */
  async createVideowall (canvasName, width, height, bezels) {
    return await this._createTag(canvasName, width, height, bezels);
  }

  /**
   * Create LED tag exclusively for LED page
   */
  async createLED (canvasName, width, height) {
    return await this._createTag(canvasName, width, height);
  }
}

export const FindPlayerSchedulesByLEDTag = (ledTag, schedules) => {
  const ledTagPattern = /LED:\d+x\d+.*/;

  // fallback for non-led matching
  if (!ledTag || !ledTagPattern.test(ledTag)) {
    return schedules.filter(schedule => !_.isEmpty(schedule.tags) && schedule.tags.includes(ledTag));
  }

  const parseLEDTag = utils.parseLedTag(ledTag);
  const dimension = parseLEDTag.dimension;

  // NOTE: we're checking against dimension only OR agains full tag
  return schedules.filter(schedule => {
    return !_.isEmpty(schedule.tags) &&
      (schedule.tags.includes(parseLEDTag.value) ||
        _.chain(schedule.tags)
          .filter((s) => ledTagPattern.test(s))
          .map(utils.parseLedTag)
          .map('dimension')
          .includes(dimension)
          .value()
      );
  });
};
